/**
 * The column picker's rows, cut into one group per source entity (CT-5529).
 *
 * A composite is several tables, and now that the builder offers fields from
 * all of them a flat list stops saying where anything comes from — two tables
 * can both have a `status`, and Employee has three different `employee_code`.
 * The heading answers that before the author has to read the field key.
 *
 * Derived from the columns themselves, so it needs no second source: each one
 * already carries its origin from the composite's field map.
 *
 *   - the **primary first**, because its fields need no join and are what the
 *     author is usually after;
 *   - the rest in the order each entity is first mentioned, which is the
 *     composite's own order and is stable between renders;
 *   - a column with no origin — a dataset's, or an entity with no composite
 *     behind it — lands in a single unnamed group, which the modal renders as
 *     the plain list this was before.
 *
 * It groups whatever it is handed, so filtering happens first: a group whose
 * columns the search removed disappears instead of leaving an empty heading.
 *
 * @param {Array<object>} columns Already filtered, in the order to show.
 * @returns {Array<{entityUuid: string, label: string, role: string, columns: Array}>}
 */
export function groupColumnsByEntity(columns = []) {
  const byEntity = new Map();

  columns.forEach((column) => {
    const key = column.origin?.entityUuid ?? '';
    if (!byEntity.has(key)) {
      byEntity.set(key, {
        entityUuid: key,
        label: column.origin?.table ?? '',
        role: column.origin?.role ?? '',
        columns: [],
      });
    }
    byEntity.get(key).columns.push(column);
  });

  // A stable sort, so everything that is not the primary keeps the insertion
  // order above rather than being reshuffled.
  const isPrimary = (group) => (group.role === 'PRIMARY' || !group.role ? 0 : 1);
  return [...byEntity.values()].sort((a, b) => isPrimary(a) - isPrimary(b));
}

export default groupColumnsByEntity;
